import { SupportBaseBillingVersion } from "@redotech/redo-model/team";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputProvider, groupInput, input } from "@redotech/ui/form";
import { areDatesEqual } from "@redotech/util/date";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { memo, useMemo } from "react";

export const supportBillingForm = groupInput({
  billingVersion: input<SupportBaseBillingVersion>(),
  pricePerMonth: input<string>(),
  freeTrialEndDate: input<Date | null>({ equal: areDatesEqual }),
  basePercentageDiscount: input<string>(),
  basePercentageDiscountEndDate: input<Date | null>({ equal: areDatesEqual }),
  baseDiscountInCents: input<string>(),
  baseDiscountEndDate: input<Date | null>({ equal: areDatesEqual }),
  humanTicketPriceInCents: input<string>(),
  pricePerOrderInCents: input<string>(),
});

export type SupportBillingForm = InputProvider.Form<typeof supportBillingForm>;

export type SupportBillingValue = InputProvider.Value<
  typeof supportBillingForm
>;

export const supportBillingDefault: SupportBillingValue = {
  billingVersion: SupportBaseBillingVersion.FLAT_RATE,
  pricePerMonth: "0",
  freeTrialEndDate: null,
  basePercentageDiscount: "0",
  basePercentageDiscountEndDate: null,
  baseDiscountInCents: "0",
  baseDiscountEndDate: null,
  humanTicketPriceInCents: "0",
  pricePerOrderInCents: "0",
};

export const SupportBillingCard = memo(function SupportBillingCard({
  input,
  billingStartDate,
}: {
  input: SupportBillingForm;
  billingStartDate: Date | null;
}) {
  const {
    billingVersion,
    pricePerMonth,
    freeTrialEndDate,
    basePercentageDiscount,
    basePercentageDiscountEndDate,
    baseDiscountInCents,
    baseDiscountEndDate,
    humanTicketPriceInCents,
    pricePerOrderInCents,
  } = input.inputs;

  const billingVersionOptions = useMemo(() => {
    const billingVersionRecord: Record<
      SupportBaseBillingVersion,
      {
        id: SupportBaseBillingVersion;
        type: "text";
        value: SupportBaseBillingVersion;
        text: string;
      }
    > = {
      [SupportBaseBillingVersion.FLAT_RATE]: {
        id: SupportBaseBillingVersion.FLAT_RATE,
        type: "text" as const,
        value: SupportBaseBillingVersion.FLAT_RATE,
        text: "Flat Rate",
      },
      [SupportBaseBillingVersion.BUCKETED_USAGE]: {
        id: SupportBaseBillingVersion.BUCKETED_USAGE,
        type: "text" as const,
        value: SupportBaseBillingVersion.BUCKETED_USAGE,
        text: "Bucketed Usage",
      },
      [SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE]: {
        id: SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE,
        type: "text" as const,
        value: SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE,
        text: "Simple Usage (per-ticket)",
      },
      [SupportBaseBillingVersion.PER_ORDER]: {
        id: SupportBaseBillingVersion.PER_ORDER,
        type: "text" as const,
        value: SupportBaseBillingVersion.PER_ORDER,
        text: "Per Order",
      },
    };

    return Object.values(billingVersionRecord);
  }, []);

  const selectedBillingVersionOption = useMemo(
    () =>
      billingVersionOptions.find((opt) => opt.value === billingVersion.value),
    [billingVersionOptions, billingVersion.value],
  );

  return (
    <Card radius="sm" title="Support Billing">
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium">
            Free trial end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              freeTrialEndDate.value
                ? dateToPlainDateTime(freeTrialEndDate.value).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              freeTrialEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
          <Text fontSize="xs" textColor="tertiary">
            Support features will be enabled without billing acceptance until
            this date
          </Text>
        </Flex>

        <RedoSingleSelectDropdownInput<SupportBaseBillingVersion>
          label="Billing Version"
          options={billingVersionOptions}
          optionSelected={({ item }) => {
            billingVersion.setValue(item.value);
          }}
          selectedItem={selectedBillingVersionOption}
          size="xs"
        />

        {billingVersion.value === SupportBaseBillingVersion.FLAT_RATE && (
          <RedoTextInput
            inputTypeHint="number"
            label="Subscription fee"
            min={0}
            setValue={pricePerMonth.setValue}
            step={0.01}
            value={pricePerMonth.value}
          />
        )}

        {billingVersion.value === SupportBaseBillingVersion.BUCKETED_USAGE && (
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="medium">
              Billing start date
            </Text>
            {billingStartDate ? (
              <Text fontSize="sm">
                {dateToPlainDateTime(billingStartDate).toPlainDate().toString()}
              </Text>
            ) : (
              <Text fontSize="sm" textColor="warning">
                Not set - merchant needs to sign up for billing to set the start
                date
              </Text>
            )}
          </Flex>
        )}

        {billingVersion.value ===
          SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE && (
          <Flex dir="column" gap="md">
            <RedoTextInput
              inputTypeHint="number"
              label="Human ticket price (in cents)"
              min={0}
              setValue={humanTicketPriceInCents.setValue}
              step={1}
              value={humanTicketPriceInCents.value}
            />
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Billing start date
              </Text>
              {billingStartDate ? (
                <Text fontSize="sm">
                  {dateToPlainDateTime(billingStartDate)
                    .toPlainDate()
                    .toString()}
                </Text>
              ) : (
                <Text fontSize="sm" textColor="warning">
                  Not set - merchant needs to sign up for billing to set the
                  start date
                </Text>
              )}
            </Flex>
          </Flex>
        )}

        {billingVersion.value === SupportBaseBillingVersion.PER_ORDER && (
          <Flex dir="column" gap="md">
            <RedoTextInput
              inputTypeHint="number"
              label="Price per order (in cents)"
              min={0}
              setValue={pricePerOrderInCents.setValue}
              step={1}
              value={pricePerOrderInCents.value}
            />
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Billing start date
              </Text>
              {billingStartDate ? (
                <Text fontSize="sm">
                  {dateToPlainDateTime(billingStartDate)
                    .toPlainDate()
                    .toString()}
                </Text>
              ) : (
                <Text fontSize="sm" textColor="warning">
                  Not set - merchant needs to sign up for billing to set the
                  start date
                </Text>
              )}
            </Flex>
          </Flex>
        )}

        <Text fontSize="md" fontWeight="semibold">
          Discounts
        </Text>

        <RedoTextInput
          inputTypeHint="number"
          label="Percentage discount"
          max={100}
          min={0}
          setValue={basePercentageDiscount.setValue}
          step={0.01}
          value={basePercentageDiscount.value}
        />

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Percentage discount end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              basePercentageDiscountEndDate.value
                ? dateToPlainDateTime(
                    basePercentageDiscountEndDate.value,
                  ).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              basePercentageDiscountEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
        </Flex>

        <RedoTextInput
          inputTypeHint="number"
          label="Dollar amount discount (in cents)"
          min={0}
          setValue={baseDiscountInCents.setValue}
          step={1}
          value={baseDiscountInCents.value}
        />

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Dollar discount end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              baseDiscountEndDate.value
                ? dateToPlainDateTime(baseDiscountEndDate.value).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              baseDiscountEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
        </Flex>
      </Flex>
    </Card>
  );
});
